import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { AppSidebar } from "@/components/app-sidebar";
import {
  BarChart3,
  Package,
  ShoppingCart,
  Users,
  Plus,
  Edit,
  Trash2,
  Eye,
  TrendingUp,
  Store,
  ShoppingBasket,
  CircleCheck,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import ProductForm from "@/components/forms/ProductForm";
import {
  Dialog as StoreDialog,
  DialogTrigger as StoreDialogTrigger,
  DialogContent as StoreDialogContent,
  DialogHeader as StoreDialogHeader,
  DialogTitle as StoreDialogTitle,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
  stores?: { name: string };
}

const AdminDashboard = () => {
  const [showStoreForm, setShowStoreForm] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [storeDescription, setStoreDescription] = useState("");
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeError, setStoreError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    totalStores: 0,
    sellingRequests: 0,
  });
  const [loading, setLoading] = useState(true);

  const { user, signOut, isAdmin, userProfile, userRole } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  if (!user || !isAdmin()) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gradient-card border-none shadow-independence">
          <CardContent className="text-center p-8">
            <h2 className="text-xl font-bold mb-4">Akses Ditolak</h2>
            <p className="text-muted-foreground mb-4">
              Anda tidak memiliki izin untuk mengakses halaman admin.
            </p>
            <Button onClick={signOut} variant="outline">
              Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fetchProducts = async () => {
    try {
      let query = supabase
        .from("products")
        .select("*, stores(name)")
        .order("created_at", { ascending: false });

      if (userRole === "staff_toko" && userProfile?.store_id) {
        query = query.eq("store_id", userProfile.store_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Gagal memuat data produk",
        variant: "destructive",
      });
    }
  };

  const fetchStats = async () => {
    try {
      const { count: totalProducts } = await supabase
        .from("products")
        .select("*", { count: "exact", head: true });

      const { count: activeProducts } = await supabase
        .from("products")
        .select("*", { count: "exact", head: true })
        .eq("is_active", true);

      let totalStores = 0;
      if (userRole === "superadmin") {
        const { count } = await supabase
          .from("stores")
          .select("*", { count: "exact", head: true });
        totalStores = count || 0;
      }

      let sellingRequests = 0;
      const { count } = await supabase
        .from("sell_requests")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");
      sellingRequests = count || 0;

      setStats({
        totalProducts: totalProducts || 0,
        activeProducts: activeProducts || 0,
        sellingRequests: sellingRequests || 0,
        totalStores,
      });
    } catch (error) {
      // silent
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProducts(), fetchStats()]);
      setLoading(false);
    };
    if (user && isAdmin() && userRole) {
      loadData();
    }
  }, [user, userRole]);

  const handleProductAdded = () => {
    fetchProducts();
    fetchStats();
    setEditingProduct(null);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!confirm("Apakah Anda yakin ingin menghapus produk ini?")) return;
    try {
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", productId);
      if (error) throw error;
      toast({
        title: "Produk dihapus",
        description: "Produk berhasil dihapus dari katalog",
      });
      fetchProducts();
      fetchStats();
    } catch (error: any) {
      toast({
        title: "Gagal menghapus produk",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getCategoryBadge = (category: string) => {
    const variants = {
      makanan: "bg-red-100 text-red-800",
      minuman: "bg-blue-100 text-blue-800",
      merchandise: "bg-purple-100 text-purple-800",
      kerajinan: "bg-green-100 text-green-800",
    };
    return variants[category as keyof typeof variants] || variants.makanan;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Memuat dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbLink href="/admin">Admin Panel</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem>
                  <BreadcrumbPage>Dashboard</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          {/* Quick Actions for Store */}
          {userRole === "superadmin" && (
            <div className="flex justify-end">
              <StoreDialog open={showStoreForm} onOpenChange={setShowStoreForm}>
                <StoreDialogTrigger asChild>
                  <Button variant="default">Tambah Toko</Button>
                </StoreDialogTrigger>
                <StoreDialogContent className="max-w-md">
                  <StoreDialogHeader>
                    <StoreDialogTitle>Tambah Toko Baru</StoreDialogTitle>
                  </StoreDialogHeader>
                  <form
                    className="space-y-4"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setStoreLoading(true);
                      setStoreError(null);
                      const { error } = await supabase
                        .from("stores")
                        .insert({
                          name: storeName,
                          description: storeDescription,
                        });
                      if (error) {
                        setStoreError(error.message);
                      } else {
                        setShowStoreForm(false);
                        setStoreName("");
                        setStoreDescription("");
                        fetchStats();
                        toast({ title: "Toko berhasil ditambahkan" });
                      }
                      setStoreLoading(false);
                    }}
                  >
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Nama Toko
                      </label>
                      <input
                        type="text"
                        className="w-full border rounded px-3 py-2"
                        value={storeName}
                        onChange={(e) => setStoreName(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Deskripsi Toko
                      </label>
                      <textarea
                        className="w-full border rounded px-3 py-2"
                        value={storeDescription}
                        onChange={(e) => setStoreDescription(e.target.value)}
                        rows={3}
                        required
                      />
                    </div>
                    {storeError && (
                      <p className="text-sm text-red-500 text-center">
                        {storeError}
                      </p>
                    )}
                    <Button
                      type="submit"
                      variant="default"
                      className="w-full"
                      disabled={storeLoading}
                    >
                      {storeLoading ? "Memproses..." : "Simpan"}
                    </Button>
                  </form>
                </StoreDialogContent>
              </StoreDialog>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-gradient-card border-none shadow-soft hover:shadow-independence transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Total Produk
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {stats.totalProducts}
                    </p>
                  </div>
                  <Package className="w-8 h-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-card border-none shadow-soft hover:shadow-independence transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Produk Aktif
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {stats.activeProducts}
                    </p>
                  </div>
                  <CircleCheck className="w-8 h-8 text-accent" />
                </div>
              </CardContent>
            </Card>
            {userRole === "superadmin" && (
              <Card className="bg-gradient-card border-none shadow-soft hover:shadow-independence transition-all">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Total Toko
                      </p>
                      <p className="text-2xl font-bold text-foreground">
                        {stats.totalStores}
                      </p>
                    </div>
                    <Store className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
            )}
            <Card className="bg-gradient-card border-none shadow-soft hover:shadow-independence transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Selling Request
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {stats.sellingRequests}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <Tabs defaultValue="products" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="products">Produk</TabsTrigger>
              <TabsTrigger value="analytics">Analitik</TabsTrigger>
            </TabsList>

            {/* Products Tab */}
            <TabsContent value="products">
              <Card className="bg-gradient-card border-none shadow-soft">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Manajemen Produk</CardTitle>
                      <CardDescription>
                        Kelola katalog produk bazaar kemerdekaan
                      </CardDescription>
                    </div>
                    <Dialog
                      open={showProductForm}
                      onOpenChange={setShowProductForm}
                    >
                      <DialogTrigger asChild>
                        {/* <Button variant="default">
                          <Plus className="w-4 h-4 mr-2" />
                          Tambah Produk
                        </Button> */}
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>
                            {editingProduct
                              ? "Edit Produk"
                              : "Tambah Produk Baru"}
                          </DialogTitle>
                        </DialogHeader>
                        <ProductForm
                          onProductAdded={handleProductAdded}
                          onClose={() => {
                            setShowProductForm(false);
                            setEditingProduct(null);
                          }}
                          productToEdit={editingProduct}
                        />
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {products.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Belum ada produk
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        Mulai dengan menambahkan produk pertama Anda
                      </p>
                      <Button
                        variant="default"
                        onClick={() => setShowProductForm(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Produk
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {products.map((product) => (
                        <Card
                          key={product.id}
                          className="overflow-hidden hover:shadow-soft transition-all"
                        >
                          <div className="aspect-video relative">
                            {product.image_url ? (
                              <img
                                src={product.image_url}
                                alt={product.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = "/placeholder.svg";
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-muted flex items-center justify-center">
                                <Package className="w-12 h-12 text-muted-foreground" />
                              </div>
                            )}
                            <Badge
                              className={`absolute top-2 left-2 ${getCategoryBadge(
                                product.category
                              )}`}
                            >
                              {product.category}
                            </Badge>
                            {!product.is_active ? (
                              <Badge className="absolute top-2 right-2 bg-gray-500 text-white">
                                Non-aktif
                              </Badge>
                            ) : (
                              <Badge className="absolute top-2 right-2 bg-green-500 text-white">
                                Aktif
                              </Badge>
                            )}
                          </div>
                          <CardContent className="p-4">
                            <h3 className="font-semibold text-foreground mb-1 line-clamp-1">
                              {product.name}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                              {product.description || "Tidak ada deskripsi"}
                            </p>
                            <p className="text-lg font-bold text-primary mb-3">
                              {formatPrice(product.price)}
                            </p>
                            {product.stores && (
                              <p className="text-xs text-muted-foreground mb-3">
                                Toko: {product.stores.name}
                              </p>
                            )}
                            <div className="flex space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1"
                                onClick={() =>
                                  navigate(`/admin/product/${product.id}`)
                                }
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Detail
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleEditProduct(product)}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteProduct(product.id)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              <Card className="bg-gradient-card border-none shadow-soft">
                <CardHeader>
                  <CardTitle>Analitik & Laporan</CardTitle>
                  <CardDescription>
                    Pantau performa bazaar kemerdekaan
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <BarChart3 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Grafik Analitik
                    </h3>
                    <p className="text-muted-foreground">
                      Dashboard analitik lengkap akan tersedia dalam update
                      berikutnya
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default AdminDashboard;
